import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import CurrencyInput from 'react-currency-input-field';
import useModalEffect from '../../../hooks/useModalEffect';

const MODAL_ID = 'modal-create-currency-rate';

const emptyForm = {
  currency_id: '',
  exchange_rate: '',
  start_date: '',
  end_date: ''
};

const CreateCurrencyRateForm = ({ isOpen, onClose, onSubmit, initialData, currencies, lockedCurrency }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        currency_id: initialData.currency_id ?? '',
        exchange_rate: initialData.exchange_rate ?? '',
        start_date: initialData.start_date ?? '',
        end_date: initialData.end_date ?? ''
      });
    } else {
      setFormData(emptyForm);
    }
  }, [initialData, isOpen]);

  ;

  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);


  const validate = () => {
    const errors = {};
    if (!formData.currency_id) errors.currency_id = 'Currency is required';
    const rateStr = String(formData.exchange_rate || '');
    if (!rateStr.trim()) {
      errors.exchange_rate = 'Exchange Rate is required';
    } else if (isNaN(rateStr) || parseFloat(rateStr) <= 0) {
      errors.exchange_rate = 'Exchange Rate must be a positive number';
    }
    if (!formData.start_date) errors.start_date = 'Start Date is required';
    if (!formData.end_date) errors.end_date = 'End Date is required';
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      errors.end_date = 'End Date must be after Start Date';
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    onSubmit({
      currency_id: Number(formData.currency_id),
      exchange_rate: parseFloat(String(formData.exchange_rate).replace(/,/g, '')),
      start_date: formData.start_date,
      end_date: formData.end_date
    });
  };

  return (
    <>
      <style>{`
        #${MODAL_ID} .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #${MODAL_ID} .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #${MODAL_ID} .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #${MODAL_ID} .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #${MODAL_ID} .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #${MODAL_ID} .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id={MODAL_ID}
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
      >
      <div className="modal-dialog modal-md modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{initialData ? 'Edit Currency Rate' : 'Create Currency Rate'}</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="inv-section-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                Details
              </div>
              {!lockedCurrency && (
                <div className="mb-3">
                  <label className="form-label">Currency <span className="text-danger">*</span></label>
                  <select
                    className={`form-select ${formErrors.currency_id ? 'is-invalid' : ''}`}
                    name="currency_id"
                    value={formData.currency_id}
                    onChange={handleChange}
                  >
                    <option value="">Select currency...</option>
                    {(currencies || []).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.currencyCode} — {c.currencyName}
                      </option>
                    ))}
                  </select>
                  {formErrors.currency_id && <div className="invalid-feedback">{formErrors.currency_id}</div>}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Exchange Rate (IDR) <span className="text-danger">*</span></label>
                <CurrencyInput
                  className={`form-control ${formErrors.exchange_rate ? 'is-invalid' : ''}`}
                  value={formData.exchange_rate}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, exchange_rate: value }));
                    if (formErrors.exchange_rate) setFormErrors(prev => ({ ...prev, exchange_rate: '' }));
                  }}
                  placeholder="Enter exchange rate"
                  prefix="Rp "
                  decimalsLimit={6}
                  allowNegativeValue={false}
                />
                {formErrors.exchange_rate && <div className="invalid-feedback">{formErrors.exchange_rate}</div>}
              </div>

              <div className="row">
                <div className="col-6 mb-3">
                  <label className="form-label">Start Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${formErrors.start_date ? 'is-invalid' : ''}`}
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                  />
                  {formErrors.start_date && <div className="invalid-feedback">{formErrors.start_date}</div>}
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label">End Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${formErrors.end_date ? 'is-invalid' : ''}`}
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                  />
                  {formErrors.end_date && <div className="invalid-feedback">{formErrors.end_date}</div>}
                </div>
              </div>
            </div>
            <div className="inv-footer">
              <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                <span style={{ color: '#9aa5b1' }}>
                  <span className="inv-required-star">*</span> Required fields
                </span>
              </span>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-inv-save">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  );
};

CreateCurrencyRateForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  currencies: PropTypes.array,
  lockedCurrency: PropTypes.bool
};

export default CreateCurrencyRateForm;
